import create, { StateCreator } from "zustand";

import { ClientConfig } from "src/config/client";

interface CredSlice {
  creds: Creds;
  setCreds: (creds: Creds) => void;
  valid: () => boolean;
}

const createCredSlice: StateCreator<CredSlice, [], []> = (set, get) => ({
  creds: {
    token: "",
    expires: 0,
  },
  setCreds: (creds) => set({ creds: creds }),
  valid: () => {
    const creds = get().creds;
    if (!creds?.token || !creds.expires || creds?.expires <= 0) {
      return false;
    }

    return true;
  },
});

interface ClientConfigSlice {
  clientConfig: ClientConfig;
  setClientConfig: (config: ClientConfig) => void;
}

const createClientConfigSlice: StateCreator<ClientConfigSlice, [], []> = (set) => ({
  // Base config to appease Typescript, actual values come from env var passed to client from server
  clientConfig: {
    bapiURL: null,
    refreshTokenDelay: 500,
    ipapiURL: "",
    refreshInterval: {
      disks: 10000,
    },
  },
  setClientConfig: (config) => set({ clientConfig: config }),
});

export enum DNSStatus {
  UNKNOWN = "unknown",
  ERROR = "error",
  EXIST = "exist",
  NOT_EXIST = "not_exist",
}

interface WorkflowState {
  showUpdateFormForWorkflow: Map<string, boolean>;
  showWorkflowUpdateForm: (workflow: string) => void;
  hideWorkflowUpdateForm: (workflow: string) => void;
  hideAllUpdateWorkflowForms: () => void;
  dnsRecordExists: Map<string, DNSStatus>;
  updateDnsRecordStatus: (workflow: string, status: DNSStatus) => void;
  getDnsRecordStatus: (workflow: string) => DNSStatus;
}

const createDashboardSlice: StateCreator<WorkflowState, [], []> = (set, get) => ({
  showUpdateFormForWorkflow: new Map([]),
  showWorkflowUpdateForm: (workflow: string) => {
    const current = get().showUpdateFormForWorkflow;
    set({ showUpdateFormForWorkflow: new Map(current).set(workflow, true) });
  },
  hideWorkflowUpdateForm: (workflow: string) => {
    const current = get().showUpdateFormForWorkflow;
    set({ showUpdateFormForWorkflow: new Map(current).set(workflow, false) });
  },
  hideAllUpdateWorkflowForms: () => {
    set({ showUpdateFormForWorkflow: new Map([]) });
  },
  dnsRecordExists: new Map(),
  updateDnsRecordStatus: (workflow: string, status: DNSStatus) => {
    const current = get().dnsRecordExists;
    set({ dnsRecordExists: new Map(current).set(workflow, status) });
  },
  getDnsRecordStatus: (workflow: string): DNSStatus => {
    return get().dnsRecordExists.get(workflow) || DNSStatus.UNKNOWN;
  },
});

const useCredStore = create<CredSlice & ClientConfigSlice & WorkflowState>()((...a) => ({
  ...createCredSlice(...a),
  ...createClientConfigSlice(...a),
  ...createDashboardSlice(...a),
}));

export default useCredStore;
